import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { SelectorLabelBindingDirective } from "@components/selector/selector-label-binding.directive";
import { SelectorValueBindingDirective } from "@components/selector/selector-value-binding.directive";
import { SelectorComponent } from "@components/selector/selector.component";
import { Observable, Subject, Subscription } from "rxjs";
import { debounceTime, filter, map, takeUntil } from "rxjs/operators";
import { CertifikatTyper, RaskRoller } from "~app/common/models/constants";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KontraktCertifikat, KontraktCertifikatEditDTO, VKCertifikatDTO } from "~app/features/virkeskop/models/certifiering-models";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { AvtalMediatorService } from "~app/features/virkeskop/services/avtal-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { canEdit, updatePermissions } from "~app/role-permissions";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { VirkeskopValidationRuleDirective } from "../../../validation/directives/virkeskop-validation-rule.directive";
type CertifikatControl = keyof Omit<KontraktCertifikat, "state">;

type CertifikatSelectorItem = {
    key: string;
    value: number;
};

@Component({
    selector: "rask-kontrakt-certifikat",
    templateUrl: "./kontrakt-certifikat.component.html",
    styleUrls: ["./kontrakt-certifikat.component.scss"],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        CheckboxComponent,
        VirkeskopValidationRuleDirective,
        DisabledControlDirective,
        SelectorComponent,
        SelectorLabelBindingDirective,
        SelectorValueBindingDirective,
        DatepickerComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        updatePermissions({
            role: RaskRoller.role.Kopplanera,
            canEdit: false
        })
    ]
})
export class KontraktCertifikatComponent implements OnInit, OnDestroy {
    readonly CertifikatTyper = CertifikatTyper;

    @Input() groupName: string;
    @Input() label: string;
    @Input() listObservable: Observable<VKCertifikatDTO[]>;
    @Output() updated = new EventEmitter<KontraktCertifikat>();

    protected certifikat = new KontraktCertifikat();
    protected certifikatList: Observable<CertifikatSelectorItem[]>;
    protected form: UntypedFormGroup;
    protected kontrakt: Kontrakt;

    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly fscLink = "https://search.fsc.org/en/";
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly pefcLink = "https://pefc.se/sok-certifierade-foretag";

    private formValueChangeSubscription: Subscription;
    private kontraktReadonly = false;
    private readonly canEdit = canEdit();

    constructor() {
        this.form = this.formBinderService.CreateGroup(this.certifikat);
    }

    @Input()
    set dto(dto: KontraktCertifikatEditDTO) {
        this.initForm(dto);
    }

    protected get currentSelectedId() {
        return this.form.controls.certifikatId?.getRawValue();
    }

    protected get kontraktGuid() {
        return this.kontraktMediatorService.kontrakt.guid;
    }

    protected get readonly() {
        return this.kontraktReadonly
            || !this.canEdit();
    }

    protected get today() {
        return new Date();
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
    }

    ngOnInit() {
        this.certifikatList = this.listObservable?.pipe(
            map(dto => this.getSelectorItems(dto, this.groupName)));

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngDestroyed$)).subscribe(readonly => this.kontraktReadonly = readonly);

        this.kontraktMediatorService.kontrakt$
            .pipe(filter(k => k !== null),
                takeUntil(this.ngDestroyed$))
            .subscribe(k => {
                this.kontrakt = k;
            });

        this.certifikat.certifikatstyp = this.groupName;
    }

    protected getLink = (): string => {
        if (this.groupName === CertifikatTyper.Pefc) {
            return this.pefcLink;
        }

        if (this.groupName === CertifikatTyper.Fsc) {
            return this.fscLink;
        }

        return "";
    };

    protected getSelectorItems = (dto: VKCertifikatDTO[], typ: string): CertifikatSelectorItem[] => {
        const currentSelectedId = this.currentSelectedId;
        return dto
            .filter(c => (c.aktiv &&
                c.certifikatstyp === typ &&
                (!ObjectUtils.hasValue(c.giltigTom) || new Date(c.giltigTom) > DateUtils.now())
            ) || currentSelectedId === c.id)
            .map(c => ({
                key: `${c.certifikatsnummer} - ${c.agare}`,
                value: c.id
            }));
    };

    protected numberChanged(value: number) {
        if (!value) {
            this.setControlValue("kontrolldatum", null);
            return;
        }

        if (!this.certifikat.kontrolldatum) {
            this.setControlValue("kontrolldatum", this.today.toISOString());
        }
    }

    protected selectedChanged(value: boolean) {
        if (value === false) {
            this.setControlValue("certifikatId", null);
            this.setControlValue("kontrolldatum", null);
        }
    }

    private initForm(dto: KontraktCertifikatEditDTO) {
        this.formValueChangeSubscription?.unsubscribe();
        this.setControlValue("selected", dto.selected);
        this.setControlValue("certifikatId", dto?.certifikatId ?? null);
        this.setControlValue("kontrolldatum", dto?.kontrolldatum ?? null);

        setTimeout(() => {
            this.formValueChangeSubscription = this.form.valueChanges.pipe(
                // debounce pga det emitades 3 ggr när man kryssade ur en certifikatstyp. Då triggas också ändringar på certifikatId och kontrolldatum, se selectedChanged()
                debounceTime(50)
            ).subscribe(() => this.onUpdate());
        }, 0);
    }

    private onUpdate() {
        this.updated.emit(this.certifikat);
    }

    private setControlValue(control: CertifikatControl, value: any) {
        this.form.controls[control].setValue(value);
    }
}
